import { domToPng } from "modern-screenshot";
import { Button } from "exo-ds/button/vanilla";
import { Checkbox } from "exo-ds/checkbox/vanilla";
import { Icon } from "exo-ds/icon/vanilla";
import { Select } from "exo-ds/select/vanilla";
import { TextInput } from "exo-ds/text-input/vanilla";
import { Textarea } from "exo-ds/textarea/vanilla";
import { h, logo } from "./dom.js";
import { WIZARDS } from "./actions/index.js";
import { mountWizard, checklistProgress, isDone } from "./wizard.js";
import { coverage } from "./lib/coverage.js";
import { courtContext } from "./lib/courtlayer.js";
import {
  addEnvelope, addForm, addAction, caseFacts, caseKey, caseActions, caseActionKey, caseWizard, createCase, deleteCase, envelopeOf, getCase,
  listCases, moveAction, removeEnvelope, removeForm, removeAction, saveCaseFacts, updateCase,
} from "./cases.js";
import { formStore } from "./forms.js";
import { citeText, configureRules, documentFor, loadProvisions, provisionItem, provisionsOf } from "./rules.js";
import { parseCitation } from "./lib/citations.js";

const load = (p) => fetch(p).then((r) => { if (!r.ok) throw new Error(`${p}: ${r.status}`); return r.json(); });

// Content (data/documents, data/forms) is served from the R2 bucket's CDN everywhere, local dev
// included. ?content=local reads it from this folder's data/ instead, for a local copy made with
// scripts/pull-data.sh.
const CONTENT_BASE = new URLSearchParams(location.search).get("content") === "local" ? "" : "https://cdn.raunaqgupta.com";
const contentUrl = (file) => (CONTENT_BASE ? `${CONTENT_BASE}/${file}` : file);
// Feedback goes to functions/api/feedback.js (a Cloudflare Pages Function), which opens a GitHub issue with it,
// so visitors don't need a GitHub account. Where that isn't running (local dev), the form offers a prefilled
// GitHub issue instead.
const FEEDBACK_URL = "api/feedback";
const FEEDBACK_REPO = "raunaqgupta/opencourt";
const FEEDBACK_MAX = 5000; // characters; functions/api/feedback.js enforces the same limit
const SCREENSHOT_MAX = 4_000_000; // characters of the PNG data URL; functions/api/feedback.js enforces the same limit

const app = document.getElementById("app");
let courts = [];
let actions = [];
let documents = [];
let actionProvisions = {}; // action id -> the citations of the provisions it rests on (scripts/action-provisions.mjs)
let forms = []; // the forms loaded so far (formsStore.all): those of the issuers that cover a court opened so far
let formsStore = null;
let formIds = {}; // ids forms used to have -> their id now, so bookmarked form URLs still open
let actionIds = {}; // ids of names merged into another action -> that action's id
let actionTypes = {}; // ActionType id -> { id, name, area, kind }
let docKinds = {}; // document kind id -> { id, label, description } (document-kinds.json)
let issuers = [];
let locations = []; // the places a court sits, when it sits in more than one (locations.json)
let judges = []; // judges whose own practices a court's cases follow (judges.json)
let cov = coverage([]); // resolution over jurisdictions.json, set once it loads
let jurisdictionName = {}; // jurisdictions.json node id -> name

/* ------------------------------------------------------------- routing
 * The court's reference sections: #/<courtId>/<actions|documents|forms>/<itemId>
 * Cases are kept apart from the courts, at #/cases (#233): a case is in a court, which decides the rules, actions and
 * forms it offers, but that is a property of the case rather than part of its URL. #/cases/<caseId> opens one, showing
 * its checklist, also at #/cases/<caseId>/checklist; its own actions and forms are at
 * #/cases/<caseId>/<actions|forms>/<itemId>. #/cases/new starts one, in no court until one is picked.
 * A form's PDF opens in the pane at <the form's path>/view/<file name> (#232), the name its file has in the bucket.
 * The case paths are built only by caseHref and goCase, so a later scope (such as a team's) can prefix them there.
 */
// courts.json lists which sections a court has content for; a court without the field has all of them.
// A court without its own action list still gets Actions when an action covers it, such as a
// statewide wizard (its action covers "ca"; see scripts/build-content.py).
// Cases are the user's own, so every court has them.
const hasSection = (court, section) => section === "cases" || !court || !court.sections || court.sections.includes(section)
  || (section === "actions" && courtActions(court).length > 0);
// courts.json is alphabetical; San Francisco is the court with the most content, so it opens first.
const DEFAULT_COURT = "sf-superior";
const defaultCourt = () => courts.find((c) => c.id === DEFAULT_COURT) || courts[0];
// A new case isn't in a court yet (its first steps pick one), so its page has no court in it. The rest of
// the page (the rail's links, the court the stepper suggests) uses the court last shown.
const NEW_CASE_HASH = "#/cases/new";
let lastCourtId = "";
const lastCourt = () => courts.find((c) => c.id === lastCourtId) || defaultCourt();
// The court a case page shows: the open case's own, else (the case list, a new case, a case not saved here) the last one.
const caseCourt = (k) => (k && courts.find((c) => c.id === k.court)) || lastCourt();
function route() {
  // An action opens on its provisions; #/<court>/actions/<id>/wizard, or #/cases/<case>/actions/<id>/wizard, on its wizard.
  const [, first, ...rest] = location.hash.split("/");
  if (first === "cases") {
    const [itemId, sub, subId, view, file] = rest;
    const r = { court: lastCourt(), section: "cases", itemId: itemId ? decodeURIComponent(itemId) : "", sub: "", subId: "", view: "", pdf: "" };
    if (r.itemId !== "new") r.court = caseCourt(getCase(r.itemId));
    if (r.itemId && ["actions", "forms"].includes(sub) && subId) Object.assign(r, { sub, subId: decodeURIComponent(subId), pdf: pdfOf(sub, view, file) });
    else if (r.itemId && sub === "checklist") r.sub = sub;
    if (r.sub === "actions" && view === "wizard") r.view = "wizard";
    return r;
  }
  const [section, itemId, view, file] = rest;
  const court = courts.find((c) => c.id === first) || defaultCourt();
  const sec = ["documents", "forms"].includes(section) ? section : "actions";
  const r = { court, section: sec, itemId: itemId && hasSection(court, sec) ? decodeURIComponent(itemId) : "", sub: "", subId: "", view: "", pdf: "" };
  if (sec === "actions" && r.itemId && view === "wizard") r.view = "wizard";
  if (r.itemId) r.pdf = pdfOf(sec, view, file);
  return r;
}
// The file name of the form PDF a route opens in the pane (#232), or "".
const pdfOf = (section, view, file) => (section === "forms" && view === "view" && file ? decodeURIComponent(file) : "");
// Old URLs are rewritten, without a history entry:
// - #/<court>/cases/... (from before cases left the court's URL, #233) to #/cases/..., the court becoming the one
//   last shown, which a new case suggests and the case list or an unsaved case shows;
// - #/<court>/processes/... and #/cases/<caseId>/processes/..., from before processes were called actions (#195);
// - #/<court>/forms/<old id> and #/<court>/actions/<old id>, to the item's id now.
function redirectOldId() {
  let parts = location.hash.split("/");
  if (parts[2] === "cases") {
    if (courts.some((c) => c.id === parts[1])) lastCourtId = parts[1];
    parts = ["#", ...parts.slice(2)];
    window.history.replaceState(null, "", parts.join("/"));
  }
  if (parts[2] === "processes" || (parts[1] === "cases" && parts[3] === "processes")) {
    parts[parts[1] === "cases" ? 3 : 2] = "actions";
    window.history.replaceState(null, "", parts.join("/"));
  }
  const [, courtId, section, itemId, ...rest] = parts;
  const old = { forms: formIds, actions: actionIds }[section];
  const id = old && itemId && old[decodeURIComponent(itemId)];
  if (id) window.history.replaceState(null, "", [`#/${courtId}/${section}/${encodeURIComponent(id)}`, ...rest].join("/"));
}
const go = (court, section, itemId) => { location.hash = `#/${court}/${section}${itemId ? "/" + encodeURIComponent(itemId) : ""}`; };
// The case list, a case, or an action or form opened inside a case.
const CASES_HASH = "#/cases";
const caseHref = (k, sub, id) => `${CASES_HASH}/${encodeURIComponent(k.id)}${sub ? `/${sub}${id ? `/${encodeURIComponent(id)}` : ""}` : ""}`;
const goCase = (k) => { location.hash = k ? caseHref(k) : CASES_HASH; };

/* ------------------------------------------------------------- UI state */
let query = "";
// "state" | "court" | "feedback" | "files" (a form's split button) | "addcase" | "case" (the case switcher)
// | "case-settings" | "add-action:<envelope id>" | "envelope:<envelope id>" | "add-form:<action id>"
// | "action:<action id>" | "kind" (the Documents list's kind filter) | null
let menuOpen = null;
let courtQuery = ""; // filter typed into the court menu
let docKind = null; // the kind the Documents list shows, or null for every kind; cleared with the search
// Collapsed list groups, keyed "<court>/<section>/<group>". Kept in memory only, so a reload expands everything.
// A search starts with every matching group expanded and tracks its own collapses, dropped when the search is cleared.
const collapsed = { browse: new Set(), search: new Set() };
const openLocations = new Set(); // courts whose "Where this court sits" group is open (it starts collapsed)
const openJudges = new Set(); // courts whose "Judges" group is open (it starts collapsed)
let revealedItem = ""; // last selected item whose group was expanded to show it
// The feedback draft survives closing the menu; it is cleared once sent. status: idle | sending | sent | failed.
// screenshot: attach a picture of the page, without the dialog, to the issue.
const feedback = { text: "", status: "idle", focus: false, screenshot: true };
let focusCaseName = false; // focus the new case's name field once its card renders
let refSection = "actions"; // the Reference section last open, where the sidebar's Reference link returns

/* ------------------------------------------------------------- helpers */
const fmtBytes = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const issuerTitle = (id) => (issuers.find((i) => i.id === id) || {}).title || id;
// Coverage is a node in the jurisdiction tree: "ca" (every California court) or a court's own node
// ("ca/sf-superior"). A court shows what covers its node or a node above it, at its level: its own
// material plus its state's statewide material (the California Rules of Court, statutes, Judicial Council forms).
const courtForms = (court) => forms.filter((f) => cov.formCovers(court, f, issuers.find((i) => i.id === f.issuer) || {}));
// The issuers whose forms a court shows, which load before the court renders (src/forms.js).
const formIssuers = (court) => issuers.filter((i) => cov.covers(court, i.coverage, i.applies_to_levels)).map((i) => i.id);
// A form keeps a history for each language, newest first, and each entry has one or more files (the
// copies under a form's other numbers). Its current entry is the newest English one.
const LANGUAGES = { en: "English", es: "Spanish", ko: "Korean", "zh-Hans": "Chinese (Simplified)", "zh-Hant": "Chinese (Traditional)",
  vi: "Vietnamese", tl: "Tagalog", fa: "Farsi", ar: "Arabic", hmn: "Hmong", ru: "Russian", km: "Khmer", pa: "Punjabi" };
const languageName = (lang) => LANGUAGES[lang] || lang;
const mainLanguage = (f) => (f.history.en ? "en" : Object.keys(f.history)[0]);
// A form's languages: its main one first, then the rest by name.
const languages = (f) => [mainLanguage(f), ...Object.keys(f.history).filter((l) => l !== mainLanguage(f)).sort((a, b) => languageName(a).localeCompare(languageName(b)))];
const current = (f) => f.history[mainLanguage(f)][0];
// Where a court files a form: its own listings, else those of the nearest node above it (the Judicial Council's category, for "ca").
const divisions = (f, court) => cov.divisions(f, court);
const courtDocuments = (court) => documents.filter((d) => cov.covers(court, d.coverage, d.applies_to_levels));
// The places a court sits, and the ones a document is for when it isn't for all of them.
const courtLocations = (court) => locations.filter((l) => l.court === court.id);
const docLocations = (d) => (d.locations || []).map((id) => locations.find((l) => l.id === id)).filter(Boolean);
// A court's judges, and the judge a judge_practice document belongs to (speck-opencourt/entities/Judge.md).
const courtJudges = (court) => judges.filter((j) => j.court === court.id);
const docJudge = (d) => (d.judge && judges.find((j) => j.id === d.judge)) || null;
const judgeLocation = (j) => (j.location && locations.find((l) => l.id === j.location)) || null;
const courtActions = (court) => actions.filter((p) => cov.covers(court, p.coverage, p.applies_to_levels));
const matches = (s) => s.toLowerCase().includes(query.trim().toLowerCase());
// An action is found by its own name, the other names it is listed under, or its type's name (the
// shared term that lines up the same step across courts, speck-opencourt/entities/ActionType.md).
const typeName = (p) => (actionTypes[p.type] || {}).name || "";
const actionMatch = (p) => (matches(p.name) ? "" : p.aliases.find(matches) || (typeName(p) && matches(typeName(p)) ? typeName(p) : null));
const SECTION_LABEL = { cases: "Cases", actions: "Actions", documents: "Documents", forms: "Forms" };
// Headings of the court picker's groups, by court level (courts.json `level`), highest level first. The federal
// courts have headings of their own.
const LEVEL_HEADING = { supreme: "Supreme Court", intermediate_appellate: "Courts of Appeal", general_trial: "Superior courts", bankruptcy: "Bankruptcy courts" };
const FEDERAL_HEADING = { intermediate_appellate: "Courts of Appeals", general_trial: "District courts" };
const LEVEL_ORDER = Object.keys(LEVEL_HEADING);
const levelHeading = (c) => (c.jurisdiction_type === "federal" && FEDERAL_HEADING[c.level]) || LEVEL_HEADING[c.level] || c.level;
const courtName = (id) => (courts.find((c) => c.id === id) || {}).name || id;
const formName = (f) => [f.form_number, f.title].filter(Boolean).join(" ");

/* ------------------------------------------------------------- sidebar
 * A rail of icons: the mark, then Cases and Reference (the court's actions, documents and forms), with
 * feedback at the foot. On a narrow screen it becomes a header row.
 */
function sidebar(r) {
  const feedbackBtn = h("button", { id: "feedback-btn", class: "side-item", "aria-label": "Feedback", title: "Feedback", "aria-haspopup": "dialog",
    onclick: (e) => {
      e.stopPropagation();
      menuOpen = "feedback";
      if (feedback.status === "sent") feedback.status = "idle";
      feedback.focus = true;
      render();
    } }, Icon({ name: "chat-bubble" }));
  const reference = r.section !== "cases";
  const item = (href, active, label, ic) => h("a", { class: "side-item" + (active ? " active" : ""), href, "aria-label": label, title: label,
    "aria-current": active ? "page" : null, onclick: () => { query = ""; } }, Icon({ name: ic }));
  return h("aside", { class: "sidebar" },
    h("div", { class: "side-brand" }, h("span", { class: "brand", role: "img", "aria-label": "OpenCourt", title: "OpenCourt" }, logo())),
    h("nav", { class: "side-group", "aria-label": "Sections" },
      item(CASES_HASH, !reference, "Cases", "backpack"),
      item(`#/${r.court.id}/${refSection}`, reference, "Reference", "archive")),
    h("div", { class: "side-group side-foot" }, feedbackBtn));
}

// The system a court belongs to (a state, or the federal courts): the root of its node in the jurisdiction tree.
const systemOf = (c) => cov.ancestors(c.coverage_node).at(-1);
// Courts grouped by system and level: federal courts first, then each state by name, and within a system
// the highest level first. A group keeps the order of courts.json.
function groupCourts(list) {
  const levelRank = (c) => (LEVEL_ORDER.includes(c.level) ? LEVEL_ORDER.indexOf(c.level) : LEVEL_ORDER.length);
  const courtRank = (a, b) => (b.jurisdiction_type === "federal") - (a.jurisdiction_type === "federal")
    || (jurisdictionName[systemOf(a)] || "").localeCompare(jurisdictionName[systemOf(b)] || "")
    || levelRank(a) - levelRank(b);
  const groups = new Map();
  for (const c of [...list].sort(courtRank)) {
    const key = `${systemOf(c)} ${c.level}`;
    (groups.get(key) || groups.set(key, []).get(key)).push(c);
  }
  return [...groups.values()];
}

// A dropdown at the top of the Reference column: a button showing the current choice, and its menu.
const dropdown = (name, label, menu) => h("div", { class: "menu-wrap dropdown-wrap" },
  h("button", { class: "dropdown", "aria-haspopup": "listbox", "aria-expanded": String(menuOpen === name),
    onclick: (e) => { e.stopPropagation(); menuOpen = menuOpen === name ? null : name; render(); } },
  h("span", null, label), Icon({ name: "triangle-down" })),
  menuOpen === name ? menu() : null);

// The state (or federal system) whose courts the court dropdown lists. Picking another opens its first court.
function stateSwitcher(r) {
  const systems = [...new Set(groupCourts(courts).flat().map(systemOf))];
  const pick = (sys) => {
    menuOpen = null; query = "";
    if (sys !== systemOf(r.court)) go(courts.find((c) => systemOf(c) === sys).id, r.section);
    else render();
  };
  return dropdown("state", jurisdictionName[systemOf(r.court)] || systemOf(r.court), () =>
    h("div", { class: "menu", role: "listbox", onclick: (e) => e.stopPropagation() },
      systems.map((sys) => h("button", { role: "option", "aria-selected": String(sys === systemOf(r.court)), class: "menu-item" + (sys === systemOf(r.court) ? " sel" : ""),
        onclick: () => pick(sys) }, jurisdictionName[sys] || sys))));
}

// The court switcher: the courts of the current state, with a search for court or county names.
function courtSwitcher(r) {
  const pick = (c) => { menuOpen = null; courtQuery = ""; query = ""; go(c.id, r.section); };
  const courtMatches = () => courts.filter((c) => systemOf(c) === systemOf(r.court)
    && [c.name, (c.counties || []).join(" ")].some((s) => s.toLowerCase().includes(courtQuery.trim().toLowerCase())));
  const courtGroups = () => groupCourts(courtMatches());
  const courtOptions = () => {
    const groups = courtGroups();
    if (!groups.length) return [h("div", { class: "menu-note" }, "No courts match.")];
    return groups.map((rows, i) => h("div", { role: "group", "aria-labelledby": `court-group-${i}` },
      h("div", { id: `court-group-${i}`, class: "menu-heading", role: "presentation" }, levelHeading(rows[0])),
      ...rows.map((c) => h("button", { role: "option", "aria-selected": String(c.id === r.court.id), class: "menu-item" + (c.id === r.court.id ? " sel" : ""),
        onclick: () => pick(c) }, c.name))));
  };
  return dropdown("court", r.court.name, () => h("div", { class: "menu court-menu", onclick: (e) => e.stopPropagation() },
    TextInput({ type: "search", size: "sm", width: "full", id: "court-filter", class: "menu-filter", defaultValue: courtQuery, placeholder: "Search courts or counties", "aria-label": "Search courts",
      onInput: (e) => { courtQuery = e.target.value; document.getElementById("court-options").replaceChildren(...courtOptions()); },
      onKeydown: (e) => { if (e.key === "Enter") { const first = courtGroups()[0]; if (first) pick(first[0]); } } }),
    h("div", { id: "court-options", class: "menu-options", role: "listbox" }, courtOptions())));
}

// The Documents list's kind filter: every kind, or one of the kinds the court's documents have, in the order of document-kinds.json.
function kindFilter(r) {
  const kinds = Object.keys(docKinds).filter((k) => courtDocuments(r.court).some((d) => d.kind === k));
  const pick = (k) => { menuOpen = null; docKind = k; render(); };
  const option = (k, label) => h("button", { role: "option", "aria-selected": String(docKind === k), class: "menu-item" + (docKind === k ? " sel" : ""),
    title: k ? docKinds[k].description : null, onclick: () => pick(k) }, label);
  return dropdown("kind", `Kind: ${docKind ? docKinds[docKind].label : "All"}`, () =>
    h("div", { class: "menu", role: "listbox", onclick: (e) => e.stopPropagation() }, option(null, "All"), kinds.map((k) => option(k, docKinds[k].label))));
}

// Actions, Documents and Forms, as a segmented control under the court switcher.
const referenceTabs = (r) => h("nav", { class: "tabs", "aria-label": "Reference" },
  ["actions", "documents", "forms"].map((sec) => h("a", { class: "tab" + (r.section === sec ? " active" : ""), href: `#/${r.court.id}/${sec}`,
    "aria-current": r.section === sec ? "page" : null, onclick: () => { query = ""; } }, SECTION_LABEL[sec])));

// A feedback issue records the page it was sent from (court, section and item), never wizard answers,
// which stay in this browser.
const feedbackPage = () => location.hash || "#/";
const githubIssueUrl = () => {
  const body = `${feedback.text.trim()}\n\n---\nPage: ${location.origin}${location.pathname}${feedbackPage()}`;
  return `https://github.com/${FEEDBACK_REPO}/issues/new?labels=feedback&title=${encodeURIComponent("Feedback: " + feedbackTitle(feedback.text))}&body=${encodeURIComponent(body)}`;
};
const feedbackTitle = (text) => { const line = text.trim().split("\n")[0]; return line.length > 60 ? line.slice(0, 57).trimEnd() + "..." : line; };

// A PNG of the app as it is on screen, leaving out the feedback dialog, or null if it can't be taken.
// A retina screen is captured at 2x, and at 1x if that is too big to send.
async function pageScreenshot() {
  const opts = { width: innerWidth, height: innerHeight, backgroundColor: getComputedStyle(document.body).backgroundColor,
    filter: (n) => !(n instanceof Element && n.classList.contains("feedback-modal")), features: { restoreScrollPosition: true } };
  try {
    for (const scale of [...new Set([Math.min(devicePixelRatio || 1, 2), 1])]) {
      const png = await domToPng(app, { ...opts, scale });
      if (png.length <= SCREENSHOT_MAX) return png;
    }
  } catch (e) {
    console.warn("Couldn't take the feedback screenshot", e);
  }
  return null;
}

async function sendFeedback() {
  if (!feedback.text.trim() || feedback.status === "sending") return;
  feedback.status = "sending"; render();
  try {
    const screenshot = feedback.screenshot ? await pageScreenshot() : null;
    const res = await fetch(FEEDBACK_URL, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: feedback.text.trim(), page: feedbackPage(), ...(screenshot ? { screenshot } : {}) }) });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(out.error || `HTTP ${res.status}`);
    Object.assign(feedback, { text: "", status: "sent" });
  } catch {
    feedback.status = "failed";
  }
  render();
}

// Feedback opens as a modal dialog in the middle of the page. Escape, the close button or a click
// outside it closes it, keeping the draft, and focus returns to the Feedback button.
function closeFeedback() {
  menuOpen = null;
  render();
  const btn = document.getElementById("feedback-btn");
  if (btn) btn.focus();
}

function feedbackDialog() {
  const dlg = h("dialog", { class: "modal feedback-modal", "aria-labelledby": "feedback-title",
    onclick: (e) => { e.stopPropagation(); if (e.target === dlg) closeFeedback(); },
    oncancel: (e) => { e.preventDefault(); closeFeedback(); } },
  h("div", { class: "modal-head" },
    h("h2", { id: "feedback-title", class: "modal-title" }, "Send feedback"),
    h("button", { type: "button", class: "icon-btn", "aria-label": "Close", onclick: closeFeedback }, Icon({ name: "cross-2" }))),
  feedbackPanel());
  return dlg;
}

function feedbackPanel() {
  if (feedback.status === "sent") {
    return h("div", { class: "feedback-body" },
      h("p", { class: "feedback-done" }, "Thanks, your feedback was sent."),
      h("div", { class: "feedback-actions" }, Button({ onClick: closeFeedback, children: "Close" })));
  }
  const sending = feedback.status === "sending";
  const send = Button({ type: "submit", variant: "primary", disabled: sending || !feedback.text.trim(), children: sending ? "Sending..." : "Send" });
  return h("form", { class: "feedback-body", onsubmit: (e) => { e.preventDefault(); sendFeedback(); } },
    h("label", { class: "visually-hidden", for: "feedback-text" }, "Feedback"),
    Textarea({ id: "feedback-text", width: "full", rows: "5", maxlength: String(FEEDBACK_MAX), disabled: sending, defaultValue: feedback.text,
      placeholder: "What's wrong, missing or confusing?",
      onInput: (e) => { feedback.text = e.target.value; send.disabled = !feedback.text.trim(); } }),
    Checkbox({ size: "sm", class: "feedback-check", defaultChecked: feedback.screenshot, disabled: sending,
      onChange: (e) => { feedback.screenshot = e.target.checked; }, children: "Attach a screenshot of the page" }),
    h("div", { class: "menu-note" }, "Leave out names and case details. Your wizard answers aren't sent, except what the screenshot shows."),
    feedback.status === "failed"
      ? h("div", { class: "feedback-error", role: "alert" }, "Couldn't send it. ",
        h("a", { href: githubIssueUrl(), target: "_blank", rel: "noopener noreferrer", onclick: (e) => { e.currentTarget.href = githubIssueUrl(); } },
          "Open it as a GitHub issue instead"), ".")
      : null,
    h("div", { class: "feedback-actions" }, Button({ onClick: closeFeedback, children: "Cancel" }), send));
}

/* ------------------------------------------------------------- list column */
function searchBox(r) {
  return TextInput({ type: "search", width: "full", defaultValue: query, placeholder: `Search ${r.section}`, "aria-label": "Search",
    icon: Icon({ name: "magnifying-glass" }), iconPosition: "end", onInput: (e) => { query = e.target.value; renderList(r); } });
}

function listBody(r) {
  if (r.section === "cases") return caseNav(r, getCase(r.itemId));
  if (!hasSection(r.court, r.section)) return [h("p", { class: "empty" }, `${SECTION_LABEL[r.section]} for ${r.court.name} aren't available yet.`)];
  if (r.section === "actions") {
    // An action found by another name shows that name under its own.
    const rows = courtActions(r.court).map((p) => [p, actionMatch(p)]).filter(([, m]) => m !== null);
    if (!rows.length) return [h("p", { class: "empty" }, "No actions match your search.")];
    const items = rows.map(([p, via]) => h("a", { class: "row" + (p.id === r.itemId ? " sel" : ""), href: `#/${r.court.id}/actions/${encodeURIComponent(p.id)}`,
      "data-letter": letterOf(p.name) },
    h("span", { class: "row-text" }, h("span", { class: "row-name" }, p.name), via ? h("span", { class: "row-sub" }, via) : null),
    p.wizard ? h("span", { class: "pill" }, "Wizard") : null));
    return [letterBar(items), h("div", { class: "rows" }, items)];
  }
  if (r.section === "forms") {
    const rows = courtForms(r.court).filter((f) => matches(f.title) || [f.form_number, ...f.other_numbers, ...divisions(f, r.court)].some(matches));
    if (!rows.length) return [h("p", { class: "empty" }, "No forms match your search.")];
    return groupedRows(r, rows, (f) => issuerTitle(f.issuer), (f) => h("a", { class: "row" + (f.id === r.itemId ? " sel" : ""), href: `#/${r.court.id}/forms/${encodeURIComponent(f.id)}`, title: f.title },
      h("span", { class: "row-name" }, f.title), f.status === "revoked" ? h("span", { class: "pill revoked" }, "Revoked") : null,
      f.form_number ? h("span", { class: "pill" }, f.form_number) : null));
  }
  const shown = courtDocuments(r.court).filter((d) => !docKind || d.kind === docKind);
  // A judge's own practices are listed with the judge, not among the court's documents.
  const rows = shown.filter((d) => !d.judge).filter((d) => matches(d.title) || matches(d.category) || docLocations(d).some((l) => matches(l.name)));
  const where = [...locationGroup(r.court), ...judgeGroup(r, shown.filter((d) => d.judge))];
  if (!rows.length) return [...where, h("p", { class: "empty" }, where.length ? "No other documents match." : "No documents match your search.")];
  return [...where, ...groupedRows(r, rows, (d) => d.category, (d) => {
    const at = docLocations(d).map((l) => l.name).join("; ");
    return h("a", { class: "row" + (d.id === r.itemId ? " sel" : ""), href: `#/${r.court.id}/documents/${encodeURIComponent(d.id)}`, title: d.title },
      h("span", { class: "row-text" }, h("span", { class: "row-name" }, d.title), at ? h("span", { class: "row-sub" }, at) : null));
  })];
}

// The A to Z strip above the action list (actions.json is alphabetical): a letter scrolls the list to its
// first action, a letter with none is disabled, and the letters of the rows shown are highlighted, one block
// for each run of adjacent letters.
const LETTERS = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];
const letterOf = (name) => name.trim().charAt(0).toUpperCase();
function letterBar(items) {
  const has = new Set(items.map((el) => el.dataset.letter));
  const bar = h("div", { class: "letters", role: "toolbar", "aria-label": "Jump to letter" }, LETTERS.map((l) => h("button", { class: "letter", "data-letter": l,
    disabled: !has.has(l), "aria-label": `Actions starting with ${l}`,
    onclick: () => {
      const body = bar.parentElement, row = items.find((el) => el.dataset.letter === l);
      if (body && row) body.scrollTop = row.offsetTop - bar.offsetHeight - 4;
    } }, l)));
  return bar;
}
// Highlights the letters of the action rows in view, one block over each run of adjacent letters; run when
// the list scrolls or renders.
function markLetter(body) {
  const bar = body.querySelector(".letters");
  if (!bar) return;
  const top = body.scrollTop + bar.offsetHeight, bottom = body.scrollTop + body.clientHeight;
  const shown = new Set([...body.querySelectorAll(".row[data-letter]")]
    .filter((el) => el.offsetTop + el.offsetHeight > top && el.offsetTop < bottom).map((el) => el.dataset.letter));
  const buttons = [...bar.querySelectorAll(".letter")];
  buttons.forEach((b) => b.classList.toggle("active", shown.has(b.dataset.letter)));
  bar.querySelectorAll(".letter-mark").forEach((m) => m.remove());
  buttons.forEach((first, i) => {
    if (!shown.has(first.dataset.letter) || shown.has(buttons[i - 1]?.dataset.letter)) return;
    let last = first;
    while (shown.has(last.nextElementSibling?.dataset.letter)) last = last.nextElementSibling;
    bar.prepend(h("span", { class: "letter-mark", "aria-hidden": "true", style: `left: ${first.offsetLeft}px; top: ${first.offsetTop}px; ` +
      `width: ${last.offsetLeft + last.offsetWidth - first.offsetLeft}px; height: ${first.offsetHeight}px` }));
  });
}

// Where a court sits, for a court that sits in more than one place: a group at the top of its Documents list,
// collapsed until opened. A search shows the locations it matches (name, address or county), open.
function locationGroup(court) {
  const searching = !!query.trim();
  const ls = courtLocations(court).filter((l) => !searching || [l.name, l.address, ...l.counties].some(matches));
  if (!ls.length) return [];
  const open = searching || openLocations.has(court.id);
  const list = h("div", { class: "group-rows", hidden: !open }, ls.map((l) => h("div", { class: "location" },
    locationLink(l), h("div", { class: "row-sub" }, l.address), l.counties.length ? h("div", { class: "location-counties" }, `Generally hears cases from ${l.counties.join(", ")}`) : null)));
  const btn = h("button", { class: "group", "aria-expanded": String(open), onclick: () => {
    const nowOpen = list.hidden;
    if (!searching) { if (nowOpen) openLocations.add(court.id); else openLocations.delete(court.id); }
    btn.setAttribute("aria-expanded", String(nowOpen));
    list.hidden = !nowOpen;
  } }, Icon({ name: "caret-down" }), h("span", { class: "group-name" }, "Where this court sits"), h("span", { class: "group-count" }, `(${ls.length})`));
  return [btn, list];
}

// The court's judges and each one's own standing orders and practices: a group at the top of its Documents list,
// collapsed until opened, as they would bury the court's rules. A search shows the judges whose name, courtroom or
// location matches, with all their documents, and the documents whose title matches, open.
function judgeGroup(r, docs) {
  const searching = !!query.trim();
  const judged = courtJudges(r.court).map((j) => {
    const own = docs.filter((d) => d.judge === j.id);
    const l = judgeLocation(j);
    const hit = !searching || [j.name, j.title, j.assignment_unit, l ? l.name : ""].some((s) => s && matches(s));
    return [j, hit ? own : own.filter((d) => matches(d.title)), hit];
  }).filter(([j, own, hit]) => hit || own.length).filter(([j, own]) => !docKind || own.length);
  if (!judged.length) return [];
  const sel = judged.some(([, own]) => own.some((d) => d.id === r.itemId));
  const open = searching || sel || openJudges.has(r.court.id);
  const list = h("div", { class: "group-rows", hidden: !open }, judged.map(([j, own]) => {
    const l = judgeLocation(j);
    return h("div", { class: "judge" },
      h("a", { class: "judge-name", href: j.official_page, target: "_blank", rel: "noopener noreferrer" }, j.name),
      h("div", { class: "row-sub" }, [j.title, j.assignment_unit, l ? l.name : null].filter(Boolean).join(" · ")),
      own.map((d) => h("a", { class: "row" + (d.id === r.itemId ? " sel" : ""), href: `#/${r.court.id}/documents/${encodeURIComponent(d.id)}`, title: d.title },
        h("span", { class: "row-name" }, d.title))));
  }));
  const btn = h("button", { class: "group", "aria-expanded": String(open), onclick: () => {
    const nowOpen = list.hidden;
    if (!searching) { if (nowOpen) openJudges.add(r.court.id); else openJudges.delete(r.court.id); }
    btn.setAttribute("aria-expanded", String(nowOpen));
    list.hidden = !nowOpen;
  } }, Icon({ name: "caret-down" }), h("span", { class: "group-name" }, "Judges"), h("span", { class: "group-count" }, `(${judged.length})`));
  return [btn, list];
}

// Rows under collapsible group headings, each counting the rows that match the current search.
function groupedRows(r, rows, groupOf, rowEl) {
  const groups = new Map();
  rows.forEach((x) => { const g = groupOf(x); if (!groups.has(g)) groups.set(g, []); groups.get(g).push(x); });
  if (!query.trim()) collapsed.search.clear();
  const set = query.trim() ? collapsed.search : collapsed.browse;
  const out = [];
  groups.forEach((items, g) => {
    const key = `${r.court.id}/${r.section}/${g}`;
    // Expand the selected item's group once when it's selected, so it can still be collapsed afterwards.
    if (r.itemId && r.itemId !== revealedItem && items.some((x) => x.id === r.itemId)) {
      collapsed.browse.delete(key); collapsed.search.delete(key); revealedItem = r.itemId;
    }
    const open = !set.has(key);
    const list = h("div", { class: "group-rows", hidden: !open }, items.map(rowEl));
    const btn = h("button", { class: "group", "aria-expanded": String(open), onclick: () => {
      const nowOpen = set.has(key);
      if (nowOpen) set.delete(key); else set.add(key);
      btn.setAttribute("aria-expanded", String(nowOpen));
      list.hidden = !nowOpen;
    } }, Icon({ name: "caret-down" }), h("span", { class: "group-name" }, g), h("span", { class: "group-count" }, `(${items.length.toLocaleString("en-US")})`));
    out.push(btn, list);
  });
  return out;
}

// The case switcher's order: active cases, most recently changed first, then archived ones.
const caseOrder = () => { const ks = listCases(); return [...ks.filter((k) => !k.archived), ...ks.filter((k) => k.archived)]; };

// The top of the case column: the case switcher (every saved case, in any court, and "Create new case")
// and the case's gear menu (rename, archive, delete).
function caseHead(r, k) {
  const ks = caseOrder(), archived = ks.filter((x) => x.archived);
  const caseItem = (x) => h("a", { class: "menu-item" + (k && x.id === k.id ? " sel" : ""), role: "menuitem", href: caseHref(x),
    title: `${x.name} (${courtName(x.court)})`, onclick: () => { menuOpen = null; } }, x.name);
  const caseMenu = menuOpen === "case"
    ? h("div", { class: "menu case-menu", role: "menu", onclick: (e) => e.stopPropagation() },
      h("div", { class: "case-menu-list" },
        ks.filter((x) => !x.archived).map(caseItem),
        archived.length ? [h("div", { class: "menu-heading" }, "Archived"), archived.map(caseItem)] : null),
      h("a", { class: "menu-item", role: "menuitem", href: NEW_CASE_HASH, onclick: () => { menuOpen = null; } }, Icon({ name: "plus" }), "Create new case"))
    : null;
  const switcher = h("div", { class: "menu-wrap" },
    h("button", { class: "case-switch", "aria-haspopup": "menu", "aria-expanded": String(menuOpen === "case"),
      onclick: (e) => { e.stopPropagation(); menuOpen = menuOpen === "case" ? null : "case"; render(); } },
    h("span", null, k ? k.name : "Choose a case"), Icon({ name: "triangle-down" })),
    caseMenu);
  const rename = () => {
    menuOpen = null;
    const name = prompt("Rename the case", k.name);
    if (name && name.trim()) updateCase(k.id, (c) => { c.name = name.trim(); });
    render();
  };
  const archive = () => { menuOpen = null; updateCase(k.id, (c) => { c.archived = !c.archived; }); render(); };
  const remove = () => {
    menuOpen = null;
    if (!confirm(`Delete the case "${k.name}", with its checklist and the answers of every action in it, from this browser?`)) { render(); return; }
    deleteCase(k.id);
    goCase();
  };
  const gearMenu = menuOpen === "case-settings"
    ? h("div", { class: "menu settings-menu", role: "menu", onclick: (e) => e.stopPropagation() },
      h("button", { class: "menu-item", role: "menuitem", onclick: rename }, "Rename case"),
      h("button", { class: "menu-item", role: "menuitem", onclick: archive }, k.archived ? "Unarchive case" : "Archive case"),
      h("button", { class: "menu-item", role: "menuitem", onclick: remove }, "Delete case"))
    : null;
  return h("div", { class: "list-head case-head" }, switcher,
    h("div", { class: "menu-wrap" },
      h("button", { class: "icon-btn", "aria-label": "Case settings", title: "Case settings", "aria-haspopup": "menu", "aria-expanded": String(menuOpen === "case-settings"),
        onclick: (e) => { e.stopPropagation(); menuOpen = menuOpen === "case-settings" ? null : "case-settings"; render(); } }, Icon({ name: "gear" })),
      gearMenu));
}

// Collapsed envelopes of the case column, by id, in memory only.
const caseCollapsed = new Set();

// Whether form `f` is one action `pid` uses: its wizard's checklist links it, or forms.json ties it to the action.
const usesForm = (pid, f) => (WIZARDS[pid] && WIZARDS[pid].forms.includes(f.id)) || (f.actions || []).includes(pid);

// The case column: its checklist, then its envelopes, each holding its actions and each action the forms filed
// with it. An envelope's + adds an action to it and an action's + a form to it;
// their ... menus rename or remove an envelope, and move an action to another envelope or remove it.
function caseNav(r, k) {
  const actionOf = (id) => courtActions(r.court).find((p) => p.id === id);
  const formOf = (id) => courtForms(r.court).find((f) => f.id === id);
  // A change that removes the open action or form from the case (#230) opens the case's checklist instead.
  const change = (fn) => {
    const c = updateCase(k.id, fn);
    menuOpen = null;
    const open = !c || (r.sub === "actions" ? caseActions(c).includes(r.subId)
      : r.sub === "forms" ? c.envelopes.some((e) => e.actions.some((p) => p.forms.includes(r.subId))) : true);
    if (!open) window.history.replaceState(null, "", caseHref(c, "checklist"));
    render();
  };
  const inCase = new Set(caseActions(k));
  // An icon button opening a menu (or a dialog, for the pickers) below it, open while menuOpen is `name`.
  const menuBtn = (name, label, ic, content, popup = "menu") => h("div", { class: "menu-wrap" },
    h("button", { class: "icon-btn", "aria-label": label, title: label, "aria-haspopup": popup, "aria-expanded": String(menuOpen === name),
      onclick: (e) => { e.stopPropagation(); menuOpen = menuOpen === name ? null : name; render(); } }, Icon({ name: ic })),
    menuOpen === name ? h("div", { class: `menu ${popup === "dialog" ? "add-menu" : "row-menu"}`, role: popup === "menu" ? "menu" : null, onclick: (e) => e.stopPropagation() }, content()) : null);
  const row = (sub, id, ic, name, status, tools, extra = "") => h("div", { class: `case-item${extra}` + (r.sub === sub && r.subId === id ? " sel" : ""), "data-sub": sub },
    h("a", { class: "case-item-main", href: caseHref(k, sub, id), title: name }, ic, h("span", { class: "case-item-name" }, name)),
    status ? h("span", { class: "case-item-status" }, status) : null,
    h("div", { class: "case-item-tools" }, tools));
  const removeBtn = (label, fn) => h("button", { class: "icon-btn", "aria-label": label, title: label, onclick: () => change(fn) }, Icon({ name: "cross-2" }));
  const formRow = (f, p) => row("forms", f.id, Icon({ name: "clipboard" }), formName(f), "",
    removeBtn(`Remove ${formName(f)} from ${p.name}`, (c) => removeForm(c, f.id, p.id)), " case-form");
  // Adding a form to action `pid`: first the forms it uses that it doesn't have yet, then a search of every form
  // the court shows by number, title or division, listing the forms the action uses first and revoked forms last.
  const formAdder = (pid) => () => {
    const have = envelopeOf(k, pid).actions.find((p) => p.id === pid).forms;
    const chips = courtForms(r.court).filter((f) => f.status === "current" && !have.includes(f.id) && usesForm(pid, f));
    return [
      chips.length ? h("div", { class: "suggested" }, h("span", { class: "row-sub" }, `Used by ${actionOf(pid).name}:`),
        chips.map((f) => h("button", { class: "chip", title: `Add ${formName(f)}`, onclick: () => change((c) => addForm(c, f.id, pid)) }, Icon({ name: "plus" }), f.form_number || f.title))) : null,
      picker("Add a form", "Add a form by number or title",
        courtForms(r.court).filter((f) => !have.includes(f.id)).map((f) => ({ id: f.id, label: formName(f), sub: f.status === "revoked" ? "Revoked" : "", text: [...formNumbers(f), f.title, ...divisions(f, r.court)].join(" "),
          topic: divisions(f, r.court).join(" "), rank: (usesForm(pid, f) ? 0 : 1) + (f.status === "revoked" ? 2 : 0) })),
        (id) => change((c) => addForm(c, id, pid))),
    ];
  };
  const actionAdder = (eid) => () => picker("Add an action", "Add an action by name",
    courtActions(r.court).filter((p) => !inCase.has(p.id)).map((p) => ({ id: p.id, label: p.name, sub: WIZARDS[p.id] ? "Wizard" : "", text: [p.name, ...p.aliases, typeName(p)].join(" ") })),
    (id) => change((c) => addAction(c, id, eid)));
  const actionRow = (e, ep) => {
    const p = actionOf(ep.id);
    if (!p) return null;
    const others = k.envelopes.filter((x) => x.id !== e.id);
    const menu = () => [
      others.map((x) => h("button", { class: "menu-item", role: "menuitem", onclick: () => change((c) => moveAction(c, p.id, x.id)) }, `Move to ${x.name}`)),
      e.actions.length > 1 ? h("button", { class: "menu-item", role: "menuitem", onclick: () => change((c) => moveAction(c, p.id, addEnvelope(c).id)) }, "Move to a new envelope") : null,
      h("button", { class: "menu-item", role: "menuitem", onclick: () => change((c) => removeAction(c, p.id)) }, "Remove from the case"),
    ];
    return [
      row("actions", p.id, Icon({ name: "radiobutton" }), p.name, actionStatus(r.court, k, p), [
        menuBtn(`add-form:${p.id}`, `Add a form to ${p.name}`, "plus", formAdder(p.id), "dialog"),
        menuBtn(`action:${p.id}`, `More for ${p.name}`, "dots-horizontal", menu),
      ]),
      ep.forms.map(formOf).filter(Boolean).map((f) => formRow(f, p)),
    ];
  };
  const group = (key, label, ic, rows, empty, tools) => {
    const open = !caseCollapsed.has(key);
    const body = h("div", { class: "case-items", hidden: !open }, rows.length ? rows : h("p", { class: "empty" }, empty));
    const btn = h("button", { class: "group", "aria-expanded": String(open), onclick: () => {
      const now = body.hidden;
      if (now) caseCollapsed.delete(key); else caseCollapsed.add(key);
      btn.setAttribute("aria-expanded", String(now));
      body.hidden = !now;
    } }, Icon({ name: "triangle-down" }), ic, h("span", { class: "group-name" }, label));
    return h("section", { class: "case-group" }, h("div", { class: "group-head" }, btn, h("div", { class: "group-tools" }, tools)), body);
  };
  const envelopeGroup = (e) => {
    const rename = () => {
      const name = prompt("Rename the envelope", e.name);
      if (name && name.trim()) change((c) => { c.envelopes.find((x) => x.id === e.id).name = name.trim(); });
      else { menuOpen = null; render(); }
    };
    const menu = () => [
      h("button", { class: "menu-item", role: "menuitem", onclick: rename }, "Rename envelope"),
      h("button", { class: "menu-item", role: "menuitem", disabled: e.actions.length > 0, onclick: () => change((c) => removeEnvelope(c, e.id)) }, "Remove envelope"),
      e.actions.length ? h("div", { class: "menu-note" }, "Move or remove its actions to remove it.") : null,
    ];
    return group(e.id, e.name, Icon({ name: "envelope-closed" }), e.actions.flatMap((ep) => actionRow(e, ep)).filter(Boolean), "No actions yet.", [
      menuBtn(`add-action:${e.id}`, `Add an action to ${e.name}`, "plus", actionAdder(e.id), "dialog"),
      menuBtn(`envelope:${e.id}`, `More for ${e.name}`, "dots-horizontal", menu),
    ]);
  };
  return [
    h("a", { class: "case-item case-checklist-link" + (!r.sub || r.sub === "checklist" ? " sel" : ""), href: caseHref(k, "checklist") }, "Checklist"),
    k.envelopes.map(envelopeGroup),
    h("button", { class: "case-item case-add-envelope", onclick: () => change((c) => { addEnvelope(c); }) }, Icon({ name: "plus" }), "New envelope"),
  ];
}

// How far along an action in a case is: its checklist's ticked items, once it has a checklist.
function actionStatus(court, k, p) {
  const mod = WIZARDS[p.id];
  if (!mod) return "";
  const prog = checklistProgress(caseActionKey(k.id, p.id), mod, court);
  return prog ? `${prog.done}/${prog.total}` : "";
}

function renderList(r) {
  const body = document.getElementById("list-body");
  if (body) { body.replaceChildren(...listBody(r)); markLetter(body); }
}

// Cases: the open case's column, or nothing when no case is open (no cases yet, a new case, or a case
// not saved in this browser). Reference: the state and court, the sections, the search (and on Documents the kind
// filter) and the section's list.
function listColumn(r) {
  if (r.section === "cases") {
    const k = getCase(r.itemId);
    if (!k) return null;
    return h("section", { class: "list case-col", "aria-label": `Case ${k.name}` }, caseHead(r, k),
      h("div", { id: "list-body", class: "list-body case-body" }, caseNav(r, k)));
  }
  return h("section", { class: "list", "aria-label": SECTION_LABEL[r.section] },
    h("div", { class: "list-head" }, stateSwitcher(r), courtSwitcher(r), referenceTabs(r), searchBox(r),
      r.section === "documents" && hasSection(r.court, "documents") ? kindFilter(r) : null),
    h("div", { id: "list-body", class: "list-body", onscroll: (e) => markLetter(e.currentTarget) }, listBody(r)));
}

/* ------------------------------------------------------------- detail pane */
// An action, on its own or inside case `k`, where it keeps its own answers. It opens on the provisions it rests on;
// an action with a wizard has a button at the top that opens the wizard, and the wizard one back to the provisions.
function actionPane(r, k = null) {
  const p = courtActions(r.court).find((x) => x.id === (k ? r.subId : r.itemId));
  const wrap = h("main", { class: "pane" });
  if (!p) return wrap.append(h("div", { class: "blank" }, h("p", null, "Select an action to see the provisions it rests on."))) || wrap;
  const back = k ? h("a", { class: "back", href: caseHref(k) }, Icon({ name: "arrow-left" }), k.name)
    : h("a", { class: "back", href: `#/${r.court.id}/actions` }, Icon({ name: "arrow-left" }), "All actions");
  const mod = WIZARDS[p.id];
  const wizard = mod && r.view === "wizard";
  const here = k ? caseHref(k, "actions", p.id) : `#/${r.court.id}/actions/${encodeURIComponent(p.id)}`;
  const toggle = mod ? Button({ size: "sm", href: wizard ? here : `${here}/wizard`, children: wizard ? "Provisions" : "Wizard" }) : null;
  wrap.append(back, ...(k ? [h("div", { class: "in-case" }, "In case ", h("a", { href: caseHref(k) }, k.name)), toggle ? h("div", { class: "pane-tools" }, toggle) : ""]
    : [addToCase(r, "actions", p.id, toggle)]));
  if (!wizard) {
    // The documents that govern it with no citation, among the documents this court shows.
    const sources = p.documents.filter((x) => !x.cite).map((x) => { const d = courtDocuments(r.court).find((y) => y.id === x.document); return d
      ? h("li", null, h("a", { href: `#/${r.court.id}/documents/${encodeURIComponent(d.id)}` }, d.title)) : null; }).filter(Boolean);
    const meta = [typeName(p) ? ["Type", typeName(p)] : null, p.aliases.length ? ["Also listed as", p.aliases.join("; ")] : null].filter(Boolean);
    wrap.append(h("div", { class: "wiz" }, h("div", { class: "eyebrow" }, r.court.name), h("h2", null, p.name),
      meta.length ? h("dl", { class: "action-meta" }, meta.flatMap(([key, v]) => [h("dt", null, key), h("dd", null, v)])) : null,
      actionProvisionList(r, p, !!mod),
      sources.length ? [h("h3", { class: "usedby-h" }, "Listed from"), h("ul", { class: "usedby" }, sources)] : null));
    return wrap;
  }
  const host = h("div", { class: "wiz-host" });
  wrap.append(host);
  mountWizard(host, mod, r.court, p.name, k
    ? {
      formLabel, key: caseActionKey(k.id, p.id), formHref: (id) => caseHref(k, "forms", id),
      // What another action's wizard was told about the case fills in the same questions here.
      ...(mod.caseFacts && { prefill: mod.caseFacts.from(caseFacts(k)), onAnswers: (a) => saveCaseFacts(k.id, mod.caseFacts.to(a)) }),
    }
    : { formLabel });
  return wrap;
}

// The provisions action `p` rests on (src/content/action-provisions.json: those its sources cite and, with a wizard,
// those its questions and checklists cite), by document, each with the subdivisions cited marked. A provision is
// collapsed to its citation and heading until opened.
function actionProvisionList(r, p, hasWizard) {
  const byDoc = new Map(); // document id -> { doc, refs }
  const missing = [];
  for (const ref of (actionProvisions[p.id] || []).flatMap(parseCitation)) {
    const doc = documentFor(ref.code, ref.number, r.court.id);
    if (!doc) { missing.push(`${ref.code} ${ref.number}${ref.to ? `-${ref.to}` : ""}`); continue; }
    if (!byDoc.has(doc.id)) byDoc.set(doc.id, { doc, refs: [] });
    byDoc.get(doc.id).refs.push(ref);
  }
  const order = new Map(documents.map((d, i) => [d.id, i]));
  const groups = [...byDoc.values()].sort((a, b) => order.get(a.doc.id) - order.get(b.doc.id));
  const out = [h("h3", { class: "usedby-h" }, "Provisions"),
    h("p", { class: "prov-intro" }, groups.length
      ? `The sections and rules ${hasWizard ? "this action's sources, wizard and checklist cite" : "this action's sources cite"}, with the parts cited marked.`
      : "No provisions are cited for this action yet.")];
  for (const { doc, refs } of groups) {
    const shown = courtDocuments(r.court).some((x) => x.id === doc.id);
    const holder = h("div", { class: "prov-list" }, h("p", { class: "rule-note" }, "Loading..."));
    out.push(h("section", { class: "prov-doc" },
      h("h4", { class: "prov-doc-title" }, shown ? h("a", { href: `#/${r.court.id}/documents/${encodeURIComponent(doc.id)}` }, doc.title) : doc.title),
      holder));
    loadProvisions(doc).then((f) => {
      // Each provision once, in the document's order, with every subdivision cited in it.
      const subs = new Map(), notFound = [];
      for (const ref of refs) {
        const ps = provisionsOf(ref, f);
        if (!ps.length) notFound.push(`${ref.code} ${ref.number}${ref.to ? `-${ref.to}` : ""}`);
        for (const x of ps) {
          if (!subs.has(x)) subs.set(x, new Set());
          if (x.cite === `${ref.code} ${ref.number}`) ref.subs.forEach((s) => subs.get(x).add(s));
        }
      }
      const at = new Map(f.provisions.map((x, i) => [x, i]));
      holder.replaceChildren(...[...subs].sort((a, b) => at.get(a[0]) - at.get(b[0])).map(([x, cited]) =>
        h("details", { class: "prov" },
          h("summary", null, h("b", null, x.cite, x.version ? ` (version ${x.version})` : ""), x.heading ? ` ${x.heading}` : "",
            cited.size ? h("span", { class: "prov-subs" }, ` ${[...cited].join(", ")}`) : null),
          provisionItem(x, [...cited]))),
      ...(notFound.length ? [h("p", { class: "rule-note" }, `Not found in this document: ${[...new Set(notFound)].join(", ")}.`)] : []));
    }).catch(() => {
      holder.replaceChildren(h("p", { class: "rule-note" }, `Couldn't load the text of ${[...new Set(refs.map((x) => `${x.code} ${x.number}`))].join(", ")}. `,
        shown ? h("a", { href: `#/${r.court.id}/documents/${encodeURIComponent(doc.id)}` }, "Open the document") : null));
    });
  }
  if (missing.length) out.push(h("p", { class: "rule-note" }, `Cited, but not in the documents shown here: ${[...new Set(missing)].join(", ")}.`));
  return out;
}
const formLabel = (id) => { const f = forms.find((x) => x.id === id); return f ? f.form_number || f.title : id; };

// Adds an action to one of this court's cases, in an envelope of its own, or starts a case with it. A form is
// added to an action of one of the court's cases, since every form in a case is filed with one of its actions.
function addToCase(r, kind, id, ...extra) {
  const ks = listCases().filter((k) => k.court === r.court.id);
  const open = menuOpen === "addcase";
  const done = () => { menuOpen = null; render(); };
  const startCase = () => {
    const name = prompt("Name the new case. Use a short label rather than party names; it stays in this browser.");
    if (!name || !name.trim()) return;
    const k = createCase(name.trim(), r.court.id);
    updateCase(k.id, (c) => addAction(c, id));
    menuOpen = null;
    goCase(k);
  };
  const item = (label, has, onclick) => h("button", { class: "menu-item", role: "menuitem", disabled: has, onclick }, label, has ? h("span", { class: "menu-sub" }, " Added") : null);
  const actionItems = () => [
    ks.map((k) => item(k.name, caseActions(k).includes(id), () => { updateCase(k.id, (c) => addAction(c, id)); done(); })),
    h("button", { class: "menu-item", role: "menuitem", onclick: startCase }, "New case..."),
    ks.length ? null : h("div", { class: "menu-note" }, `No cases in ${r.court.name} yet.`),
  ];
  // For a form: each case with its actions, those that use the form first.
  const f = kind === "forms" && courtForms(r.court).find((x) => x.id === id);
  const formItems = () => {
    const withActions = ks.filter((k) => caseActions(k).length);
    if (!withActions.length) return h("div", { class: "menu-note" }, ks.length ? `A form is added to an action in a case. Add an action to a case in ${r.court.name} first.` : `No cases in ${r.court.name} yet. Start one from an action, or from Cases.`);
    return withActions.map((k) => {
      const pids = caseActions(k), uses = pids.filter((pid) => f && usesForm(pid, f));
      return [h("div", { class: "menu-heading" }, k.name), [...uses, ...pids.filter((pid) => !uses.includes(pid))].map((pid) => {
        const p = courtActions(r.court).find((x) => x.id === pid);
        const has = envelopeOf(k, pid).actions.find((x) => x.id === pid).forms.includes(id);
        return p ? item(p.name, has, () => { updateCase(k.id, (c) => addForm(c, id, pid)); done(); }) : null;
      })];
    });
  };
  return h("div", { class: "pane-tools" }, extra, h("div", { class: "menu-wrap" },
    Button({ size: "sm", "aria-haspopup": "menu", "aria-expanded": String(open), onClick: (e) => { e.stopPropagation(); menuOpen = open ? null : "addcase"; render(); },
      children: [Icon({ name: "plus" }), h("span", null, "Add to case")] }),
    open ? h("div", { class: "menu add-case-menu", role: "menu", onclick: (e) => e.stopPropagation() },
      kind === "actions" ? actionItems() : formItems()) : null));
}

function documentPane(r) {
  const d = courtDocuments(r.court).find((x) => x.id === r.itemId);
  const wrap = h("main", { class: "pane" });
  if (!d) { wrap.append(h("div", { class: "blank" }, h("p", null, "Select a document to read it."))); return wrap; }
  const back = h("a", { class: "back", href: `#/${r.court.id}/documents` }, Icon({ name: "arrow-left" }), "All documents");
  const textBox = h("pre", { class: "doc-text", tabindex: "0" }, "Loading text...");
  const more = Button({ hidden: true, children: "Show full text" });
  const LIMIT = 60000;
  // A document converted into provisions (#225) shows them, under its headings; any other shows its text.
  const text = () => fetch(contentUrl(d.file)).then((res) => { if (!res.ok) throw new Error(res.status); return res.text(); }).then((t) => {
    const body = t.split("======================================================================")[1] ? t.slice(t.indexOf("======================================================================") + 72) : t;
    if (body.length > LIMIT) {
      textBox.textContent = body.slice(0, LIMIT);
      more.hidden = false;
      more.textContent = `Show full text (${fmtBytes(body.length)})`;
      more.onclick = () => { textBox.textContent = body; more.hidden = true; };
    } else textBox.textContent = body;
  });
  (d.provisions ? loadProvisions(d).then((f) => { textBox.replaceWith(provisionList(f, more)); }, text) : text()).catch(() => { textBox.textContent = CONTENT_BASE
    ? `The text file could not be loaded from ${CONTENT_BASE}. The bucket's CORS policy may not allow this site's origin (see README).`
    : "The text file could not be loaded from data/. Run scripts/pull-data.sh for a local copy, or drop ?content=local to read the CDN (see README)."; });
  wrap.append(back, h("div", { class: "doc" },
    h("div", { class: "eyebrow" }, d.category, " · ", h("span", { title: docKinds[d.kind].description }, docKinds[d.kind].label)),
    h("h2", null, d.title),
    h("dl", { class: "meta" },
      d.source ? [h("dt", null, "Source"), h("dd", null, h("a", { href: d.source, target: "_blank", rel: "noopener noreferrer" }, d.source))] : null,
      d.status ? [h("dt", null, "Status"), h("dd", null, d.status)] : null,
      docLocations(d).length ? [h("dt", null, "Location"), h("dd", null, docLocations(d).map((l) => h("div", null, locationLink(l), ", ", l.address)))] : null,
      docJudge(d) ? [h("dt", null, "Judge"), h("dd", null, h("a", { href: docJudge(d).official_page, target: "_blank", rel: "noopener noreferrer" }, docJudge(d).name),
        ", ", [docJudge(d).title, docJudge(d).assignment_unit].filter(Boolean).join(", "))] : null),
    textBox, more));
  return wrap;
}

// A converted document's provisions, each under the headings it opens (Part, Title, Chapter, Article), the first
// PROVISIONS_SHOWN of them until `more` shows the rest.
const PROVISIONS_SHOWN = 200;
function provisionList(f, more) {
  const box = h("div", { class: "doc-provisions" });
  const heading = new Map(f.headings.map((x) => [x.path.join("/"), x]));
  let at = 0, path = [];
  const add = (n) => {
    const out = [];
    for (const p of f.provisions.slice(at, at + n)) {
      // The headings this provision is under that the one before it wasn't.
      const same = p.path.findIndex((x, i) => path[i] !== x);
      for (let i = same < 0 ? p.path.length : same; i < p.path.length; i++) {
        const x = heading.get(p.path.slice(0, i + 1).join("/"));
        out.push(h(i < 2 ? "h3" : "h4", { class: "doc-heading" }, p.path[i], x && x.title ? `. ${x.title}` : ""));
      }
      path = p.path;
      out.push(provisionItem(p));
    }
    at += n;
    box.append(...out);
    more.hidden = at >= f.provisions.length;
  };
  more.textContent = `Show all ${f.provisions.length} provisions`;
  more.onclick = () => add(f.provisions.length);
  add(PROVISIONS_SHOWN);
  return box;
}

// A location's name, linking its page on the court's website.
const locationLink = (l) => h("a", { href: l.official_page, target: "_blank", rel: "noopener noreferrer" }, l.name);

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
// An ISO date as the forms print it: "May 21, 2024", "May 2024" or "2024".
const fmtDate = (iso) => { const [y, m, d] = iso.split("-"); return m ? `${MONTHS[m - 1]} ${d ? `${+d}, ` : ""}${y}` : y; };
const addMonths = (iso, n) => { const [y, m = 1, d = 1] = iso.split("-").map(Number); return new Date(Date.UTC(y, m - 1 + n, d)).toISOString().slice(0, 10); };
const formNumbers = (f) => [f.form_number, ...f.other_numbers].filter(Boolean);

// Rules that still accept a form's prior version for a while after a revision takes effect. Everyone
// else must use the current version (CRC 1.37). A revision is the date the form took effect.
const GRACE = [
  { rule: "CRC 5.504(c)(1)", months: 12,
    applies: (f) => f.issuer === "judicial-council-ca" && f.authority === "must_use" && formNumbers(f).some((n) => n.startsWith("JV-")),
    text: (until, isNew) => `Until ${until}, the court may authorize a legally accurate alternative to this form when it is produced by computer or word processor, ${isNew ? "such as an existing local form" : "including the form's immediate prior version"}` },
  { rule: "CRC 5.311(a)", months: 6,
    applies: (f, isNew) => !isNew && f.issuer === "judicial-council-ca" && formNumbers(f).some((n) => /^FL-6\d\d\b/.test(n)),
    text: (until) => `Until ${until}, a local child support agency may use and file the immediately prior version of this form in a title IV-D support action` },
];
function graceNote(f) {
  const v = current(f), today = new Date().toISOString().slice(0, 10);
  if (f.status !== "current" || !v.revision || v.revision > today) return null;
  const isNew = /^new\b/i.test(v.revision_label || "");
  const g = GRACE.find((x) => x.applies(f, isNew));
  const until = g && addMonths(v.revision, g.months);
  if (!g || until <= today) return null;
  return `${g.text(fmtDate(until), isNew)} (${g.rule}). Otherwise use the current version (CRC 1.37).`;
}

// How far the issuer's authority over a form reaches, and a court's own rule changing it (form_overrides).
const AUTHORITY = {
  must_use: "Mandatory form",
  must_accept: "Optional form; every court it applies in must accept it",
  approved: "Approved form",
  optional: "Optional form",
};
// An override only raises a form's authority (build-content.py), so it is never "optional".
const OVERRIDE = { must_use: "Mandatory", must_accept: "Accepted", approved: "Approved" };

// Relations are stored on the dependent form only, so a form also shows the court's forms that point at it.
const RELATION = { supplements: ["Supplements", "Supplemented by"], part_of: ["Part of", "Includes"] };
function relatedForms(f, court) {
  const visible = courtForms(court);
  const rows = f.relations.map((x) => [RELATION[x.kind][0], forms.find((g) => g.id === x.form)]).filter(([, g]) => g);
  visible.forEach((g) => g.relations.forEach((x) => { if (x.form === f.id) rows.push([RELATION[x.kind][1], g]); }));
  return rows.map(([kind, g]) => {
    const label = [g.form_number, g.title].filter(Boolean).join(" ");
    return [kind, visible.includes(g) ? h("a", { href: `#/${court.id}/forms/${encodeURIComponent(g.id)}` }, label) : label];
  });
}

// "Open PDF", or with several files the name it's published under ("Open PDF (ej125.pdf)" for the copy under a form's other number).
// An interview is a link to the issuer's guided interview, with no file of ours.
const openLabel = (x) => ({ pdf: "Open PDF", interview: "Start interview" }[x.format] || "Download file");
const sourceName = (x) => decodeURIComponent(new URL(x.source).pathname.split("/").pop());
const fileLabel = (x, files) => (files.length === 1 ? openLabel(x) : `${openLabel(x)} (${sourceName(x)})`);
// A PDF opens in the pane (viewHref gives its path), an interview at the issuer, and any other file downloads.
const fileName = (x) => x.file.split("/").pop();
const fileHref = (x, viewHref) => (x.format === "pdf" ? { href: viewHref(x) }
  : { href: x.format === "interview" ? x.source : encodeURI(contentUrl(x.file)), target: "_blank", rel: "noopener noreferrer" });
const fileLink = (x, label, viewHref) => h("a", { class: "menu-item", ...fileHref(x, viewHref) }, label);
const fileButton = (x, variant, label, viewHref) => Button({ variant, ...fileHref(x, viewHref), children: label });
const revisionText = (x) => x.revision_label || (x.revision ? fmtDate(x.revision) : "Undated");

// A split button: the current English file, and in its menu the other copies and each translation's
// current file, with its own revision when it isn't the English one's (a translation can lag behind).
function openButton(f, v, viewHref) {
  const [first, ...copies] = v.files, main = mainLanguage(f);
  const translations = languages(f).slice(1).map((lang) => [lang, f.history[lang]]);
  const items = [
    ...copies.map((x) => fileLink(x, sourceName(x), viewHref)),
    ...translations.flatMap(([lang, [e]]) => e.files.map((x) => fileLink(x,
      `${languageName(lang)}${e.files.length > 1 ? ` (${sourceName(x)})` : ""}${e.revision !== v.revision ? `, ${revisionText(e)}` : ""}`, viewHref))),
  ];
  const label = `${openLabel(first)}${translations.length ? ` (${languageName(main)})` : ""}${copies.length ? ` (${sourceName(first)})` : ""}`;
  if (!items.length) return fileButton(first, "primary", label, viewHref);
  return h("div", { class: "split menu-wrap" }, fileButton(first, "primary", label, viewHref),
    Button({ variant: "primary", class: "split-toggle", "aria-label": translations.length ? "Other languages" : "Other files", "aria-haspopup": "menu",
      "aria-expanded": String(menuOpen === "files"), onClick: (e) => { e.stopPropagation(); menuOpen = menuOpen === "files" ? null : "files"; render(); }, children: Icon({ name: "caret-down" }) }),
    menuOpen === "files" ? h("div", { class: "menu split-menu", role: "menu" }, items) : null);
}

// The history of one language at a time, picked from a dropdown when the form has translations.
let historyLanguage = { form: "", lang: "" };
function history(f, viewHref) {
  const langs = languages(f);
  const lang = historyLanguage.form === f.id && f.history[historyLanguage.lang] ? historyLanguage.lang : mainLanguage(f);
  if (langs.length === 1 && f.history[lang].length === 1) return null;
  const rows = (l) => f.history[l].map((x, i) => h("li", null,
    h("span", null, `${revisionText(x)}${i ? "" : " (current)"}`),
    h("div", { class: "actions" }, x.files.map((y) => fileButton(y, "secondary", fileLabel(y, x.files), viewHref)))));
  return h("details", { class: "versions" }, h("summary", null, "History"),
    langs.length > 1 ? Select({ size: "sm", class: "history-language", "aria-label": "Language", defaultValue: lang,
      options: langs.map((l) => ({ value: l, label: languageName(l) })),
      onChange: (e) => { historyLanguage = { form: f.id, lang: e.target.value }; document.getElementById("history").replaceChildren(...rows(e.target.value)); } }) : null,
    h("ul", { id: "history" }, rows(lang)));
}

// A form, on its own or inside case `k`.
function formPane(r, k = null) {
  const f = courtForms(r.court).find((x) => x.id === (k ? r.subId : r.itemId));
  const wrap = h("main", { class: "pane" });
  if (!f) { wrap.append(h("div", { class: "blank" }, h("p", null, "Select a form to open it."))); return wrap; }
  const back = k ? h("a", { class: "back", href: caseHref(k) }, Icon({ name: "arrow-left" }), k.name)
    : h("a", { class: "back", href: `#/${r.court.id}/forms` }, Icon({ name: "arrow-left" }), "All forms");
  // A form's history (its revisions and files) loads when the form opens.
  const state = formsStore.history(f, render);
  if (state !== "ready") {
    wrap.append(back, h("div", { class: "blank" }, state === "loading" ? h("p", null, "Loading the form...")
      : h("p", null, "Couldn't load this form. ", Button({ onClick: () => { formsStore.retry(f); render(); }, children: "Try again" }))));
    return wrap;
  }
  const formHref = k ? caseHref(k, "forms", f.id) : `#/${r.court.id}/forms/${encodeURIComponent(f.id)}`;
  const viewHref = (x) => `${formHref}/view/${encodeURIComponent(fileName(x))}`;
  const viewing = r.pdf && languages(f).flatMap((lang) => f.history[lang].flatMap((entry) => entry.files
    .filter((x) => x.format === "pdf" && fileName(x) === r.pdf).map((file) => ({ lang, entry, file }))))[0];
  if (viewing) return pdfPane(f, formHref, viewing);
  const usedBy = (f.actions || [])
    .map((pid) => { const p = courtActions(r.court).find((x) => x.id === pid); return p ? h("a", { href: `#/${r.court.id}/actions/${encodeURIComponent(pid)}` }, p.name) : null; })
    .filter(Boolean);
  const v = current(f), division = divisions(f, r.court).join(", ");
  const revoked = f.status === "revoked";
  // A local rule that changes whether this court requires another issuer's form (courts.json form_overrides).
  const override = (r.court.form_overrides || []).find((o) => o.form === f.id);
  const grace = graceNote(f), related = relatedForms(f, r.court);
  wrap.append(back, k ? h("div", { class: "in-case" }, "In case ", h("a", { href: caseHref(k) }, k.name)) : addToCase(r, "forms", f.id),
    h("div", { class: "doc" },
    h("div", { class: "eyebrow" }, issuerTitle(f.issuer)),
    h("h2", null, [f.form_number, f.title].filter(Boolean).join(" ")),
    revoked ? h("p", { class: "form-note revoked" }, `Revoked ${fmtDate(f.revoked)}. Kept here for reference.`) : null,
    f.note ? h("p", { class: "form-note" }, f.note) : null,
    h("dl", { class: "meta" },
      f.other_numbers.length ? [h("dt", null, "Also numbered"), h("dd", null, f.other_numbers.join(", "))] : null,
      f.authority == null && !override ? null : [h("dt", null, "Use"), h("dd", null,
        f.authority == null ? null : h("div", null, AUTHORITY[f.authority]),
        override ? h("div", { class: "override" }, `${OVERRIDE[override.authority]} in this court (${override.rule}) ${override.when}.`) : null)],
      division ? [h("dt", null, "Division"), h("dd", null, division)] : null,
      v.revision_label ? [h("dt", null, revoked ? "Last revision" : "Revision"), h("dd", null, v.revision_label)] : null,
      h("dt", null, revoked ? "Last version" : "Current version"), h("dd", null, v.files.map((x) => h("div", null, h("a", { href: x.source, target: "_blank", rel: "noopener noreferrer" }, x.source))))),
    grace ? h("p", { class: "form-note" }, grace) : null,
    h("div", { class: "actions" }, openButton(f, v, viewHref)),
    history(f, viewHref),
    related.length ? [h("h3", { class: "usedby-h" }, "Related forms"),
      h("ul", { class: "usedby" }, related.map(([kind, g]) => h("li", null, `${kind}: `, g)))] : null,
    usedBy.length ? [h("h3", { class: "usedby-h" }, "Used by these actions"),
      h("ul", { class: "usedby" }, usedBy.map((a) => h("li", null, a)))] : null));
  return wrap;
}

// One of a form's PDFs, shown in the pane by src/pdf.js under the form's name, which links back to the form.
// Below the name: the file's language when the form has others, its revision, and its published name when
// the version has several copies.
function pdfPane(f, formHref, { lang, entry, file }) {
  const url = encodeURI(contentUrl(file.file));
  const which = [languages(f).length > 1 ? languageName(lang) : null,
    `${revisionText(entry)}${entry === f.history[lang][0] ? "" : " (earlier version)"}`, entry.files.length > 1 ? sourceName(file) : null];
  return h("main", { class: "pane pane-pdf" },
    h("div", { class: "pdf-head" },
      h("div", { class: "pdf-title" },
        h("a", { class: "pdf-back", href: formHref }, Icon({ name: "arrow-left" }), h("span", null, formName(f))),
        h("div", { class: "pdf-which" }, which.filter(Boolean).join(" · "))),
      Button({ size: "sm", href: url, target: "_blank", rel: "noopener noreferrer", children: [Icon({ name: "external-link" }), h("span", null, "Open in new tab")] })),
    pdfHost(url, sourceName(file)));
}

// The open PDF stays mounted while the app re-renders around it (a menu opening, a case saved), so its pages
// aren't drawn again; render() keeps its scroll and closes it once its pane is gone. pdf.js loads with the first PDF.
let pdfOpen = null; // { url, host, close, closed }
function pdfHost(url, name) {
  if (pdfOpen && pdfOpen.url === url) return pdfOpen.host;
  closePdf();
  const open = { url, host: h("div", { class: "pdf-view" }, h("p", { class: "pdf-status" }, "Loading the PDF...")), close: null, closed: false };
  pdfOpen = open;
  import("./pdf.js").then((m) => { if (!open.closed) open.close = m.mountPdf(open.host, url, { name }); }).catch(() => {
    if (!open.closed) open.host.replaceChildren(h("p", { class: "pdf-status" }, "Couldn't load the PDF viewer. ",
      h("a", { href: url, target: "_blank", rel: "noopener noreferrer" }, "Open the PDF in a new tab"), "."));
  });
  return open.host;
}
function closePdf() {
  if (!pdfOpen) return;
  pdfOpen.closed = true;
  if (pdfOpen.close) pdfOpen.close();
  pdfOpen = null;
}

/* ------------------------------------------------------------- cases */
function casesPane(r) {
  if (r.itemId === "new") return newCasePane(r);
  const k = r.itemId && getCase(r.itemId);
  const wrap = h("main", { class: "pane" });
  if (!k) {
    // With no case open, the pane offers to start one, and says why a case in a link isn't here.
    wrap.classList.add("pane-empty");
    wrap.append(h("div", { class: "start" },
      r.itemId ? h("p", { class: "blank" }, "This case isn't saved in this browser. Cases stay in the browser they were made in.") : null,
      Button({ href: NEW_CASE_HASH, children: [Icon({ name: "plus-circled" }), listCases().length ? "Create a new case" : "Create your first case"] })));
    return wrap;
  }
  if (r.sub === "actions") return actionPane(r, k);
  if (r.sub === "forms") return formPane(r, k);
  return caseChecklist(r, k);
}

// A new case, on a card filling the pane: one stepper that asks the case's name and its court (the current one to
// begin with), then runs the case wizard for that court. Generating the checklist saves the case, with the answers
// and the actions and forms picked, and opens it. Until then the answers are a draft kept under NEW_CASE_KEY.
const NEW_CASE_KEY = "opencourt:v2:new-case";
function newCasePane(r) {
  const courtOf = (a) => courts.find((c) => c.id === a.court) || r.court;
  const ctxOf = (a) => courtContext(courtOf(a));
  // The case wizard of the court picked, made once per court.
  const made = new Map();
  const wizardOf = (a) => {
    const c = courtOf(a);
    if (!made.has(c.id)) {
      // A court picked here may not have its forms loaded yet (src/forms.js); the pane renders again once they are.
      if (!formsStore.ready(formIssuers(c))) formsStore.load(formIssuers(c)).then(render, loadFailed);
      made.set(c.id, caseWizard(null, { actions: courtActions(c), forms: courtForms(c), overrides: c.form_overrides, wizards: WIZARDS, level: c.level, system: c.coverage_node.split("/")[0] }));
    }
    return made.get(c.id);
  };
  // A step or field property of the picked court's case wizard, evaluated for that court rather than the page's.
  const lift = (get) => (a) => { const v = get(wizardOf(a)); return typeof v === "function" ? v(a, ctxOf(a)) : v; };
  const caseSteps = wizardOf({}).steps.map((st, i) => ({
    ...st,
    intro: lift((w) => w.steps[i].intro),
    skipIf: st.skipIf ? lift((w) => w.steps[i].skipIf) : undefined,
    fields: st.fields.map((f, j) => ({
      ...f,
      ...Object.fromEntries(["label", "hint", "opts", "more"].filter((p) => f[p] !== undefined).map((p) => [p, lift((w) => w.steps[i].fields[j][p])])),
      show: f.show ? lift((w) => w.steps[i].fields[j].show) : undefined,
    })),
  }));
  const courtOptions = groupCourts(courts).flatMap((rows) => rows.map((c) => ({ v: c.id, l: c.name, group: levelHeading(c) })));
  // The actions and forms picked that the picked court still offers (the court can change after they are picked).
  const picks = (a) => {
    const w = wizardOf(a), step = (id) => w.steps.find((st) => st.id === id).fields[0];
    const pf = step("actions");
    const actionOffered = new Set([...pf.opts(a, ctxOf(a)), ...(pf.more || [])].map((o) => o.v));
    const acts = (a.acts || []).filter((id) => actionOffered.has(id));
    const formOffered = new Set(step("forms").opts({ ...a, acts }, ctxOf(a)).map((o) => o.v));
    return { acts, forms: (a.forms || []).filter((id) => formOffered.has(id)) };
  };
  const wizard = {
    id: "new-case",
    steps: [
      { id: "name", title: "Name the case",
        intro: "A case keeps together the actions and forms you are preparing for one matter. Each action keeps its own checklist, and the case has a checklist of its own.",
        fields: [{ k: "name", type: "text", label: "Name", req: true, max: 80,
          hint: "For your own reference. The case is saved only in this browser, but anyone using it can see the name, so a short label is better than party names or a case number." }] },
      { id: "court", title: "Court", intro: "The court the case is in. Its actions and forms are the ones the case offers.",
        fields: [{ k: "court", type: "select", label: "Court", req: true, def: r.court.id, opts: courtOptions }] },
      ...caseSteps,
    ],
    summary: (a) => [["Name", (a.name || "").trim()], ["Court", courtOf(a).name], ...wizardOf(a).summary({ ...a, ...picks(a) })],
    build: () => null, // never shown: generating the checklist opens the case instead
  };
  const host = h("div", { class: "wiz-host" });
  mountWizard(host, wizard, r.court, "New case", {
    formLabel, key: NEW_CASE_KEY,
    cancel: { label: "Cancel", href: CASES_HASH, onclick: () => { try { localStorage.removeItem(NEW_CASE_KEY); } catch (e) { /* storage unavailable */ } } },
    onDone: (a) => {
      const c = courtOf(a), { acts, forms: fs } = picks(a);
      const k = createCase(a.name.trim(), c.id);
      updateCase(k.id, (x) => placePicks(x, acts, fs, courtForms(c)));
      const { name, court, ...answers } = a;
      try {
        localStorage.setItem(caseKey(k.id), JSON.stringify({ a: { ...answers, acts, forms: fs }, step: 0, done: true, checks: {} }));
        localStorage.removeItem(NEW_CASE_KEY);
      } catch (e) { /* storage unavailable */ }
      goCase(k);
      return false;
    },
  });
  focusCaseName = true;
  return h("main", { class: "pane pane-card" },
    h("div", { class: "card" }, h("h2", { class: "card-title" }, "New case"), host));
}

// Puts the actions and forms picked in a case wizard into case `c`: each action in an envelope of its own,
// filed at its own time until the user puts some together, with the picked forms it uses (each form with the
// first action that uses it; the wizard only offers forms its actions use). An action already in the case keeps its place.
function placePicks(c, acts, fs, courtFormList) {
  const left = new Set(fs);
  acts.forEach((pid) => {
    const mine = fs.filter((fid) => left.has(fid) && courtFormList.some((f) => f.id === fid && usesForm(pid, f)));
    if (envelopeOf(c, pid)) mine.forEach((fid) => addForm(c, fid, pid));
    else addAction(c, pid, null, mine);
    mine.forEach((fid) => left.delete(fid));
  });
}

// A case's checklist: until its wizard is done, the wizard, which picks the actions and forms to start with.
function caseChecklist(r, k) {
  const wizard = caseWizard(k.id, { actions: courtActions(r.court), forms: courtForms(r.court), overrides: r.court.form_overrides, wizards: WIZARDS, level: r.court.level, system: r.court.coverage_node.split("/")[0] });
  const setUp = isDone(caseKey(k.id));
  const host = h("div", { class: "wiz-host case-checklist" });
  const wrap = h("main", { class: "pane" },
    h("a", { class: "back", href: caseHref(k) }, Icon({ name: "arrow-left" }), k.name),
    h("div", { class: "case" },
      h("div", { class: "eyebrow" }, r.court.name, k.archived ? " · Archived" : ""),
      h("h2", null, k.name),
      setUp ? null : h("p", { class: "lead" }, "Answer a few questions to set up the case. They pick the actions and forms to start with and the deadlines that run through the whole case."),
      host));
  // Once its checklist is generated, the actions and forms picked in the wizard join the case.
  mountWizard(host, wizard, r.court, "Case", {
    formLabel, key: caseKey(k.id), formHref: (id) => caseHref(k, "forms", id),
    onDone: (a) => {
      const offered = new Set(wizard.steps.find((s) => s.id === "forms").fields[0].opts(a).map((o) => o.v));
      updateCase(k.id, (c) => placePicks(c, a.acts || [], (a.forms || []).filter((id) => offered.has(id)), courtForms(r.court)));
      render();
    },
  });
  return wrap;
}

// A search field listing every match below it, in a list that scrolls; picking one calls onPick(id). Candidates
// with a lower `rank` come first, then those whose `topic` (such as a form's division) matches, then those matching
// at the start of their label, then the rest in their order.
function picker(label, placeholder, candidates, onPick) {
  const results = h("div", { class: "picker-results" });
  const input = TextInput({ type: "search", size: "sm", width: "full", class: "menu-filter", placeholder, "aria-label": label,
    onInput: () => {
      const q = input.value.trim().toLowerCase();
      if (!q) { results.replaceChildren(); return; }
      const order = (c) => [c.rank || 0, (c.topic || "").toLowerCase().includes(q) ? 0 : 1, c.label.toLowerCase().startsWith(q) ? 0 : 1];
      const hits = candidates.filter((c) => c.text.toLowerCase().includes(q))
        .map((c, i) => ({ c, i, o: order(c) }))
        .sort((a, b) => a.o[0] - b.o[0] || a.o[1] - b.o[1] || a.o[2] - b.o[2] || a.i - b.i).map((x) => x.c);
      results.replaceChildren(...(hits.length
        ? hits.map((c) => h("button", { class: "menu-item", onclick: () => onPick(c.id) }, c.label, c.sub ? h("span", { class: "menu-sub" }, ` ${c.sub}`) : null))
        : [h("div", { class: "menu-note" }, "No matches.")]));
    },
    onKeydown: (e) => { if (e.key === "Enter") { const first = results.querySelector("button"); if (first) first.click(); } } });
  return h("div", { class: "picker" }, input, results);
}

/* ------------------------------------------------------------- render */
function render() {
  const r = route();
  if (!r.court) { app.textContent = "No courts configured."; return; }
  // A court renders once the forms of the issuers that cover it are loaded.
  if (!formsStore.ready(formIssuers(r.court))) { formsStore.load(formIssuers(r.court)).then(render, loadFailed); return; }
  // So does a form, once its history is loaded (formPane says so if it can't be).
  const formId = r.section === "forms" ? r.itemId : r.sub === "forms" ? r.subId : "";
  const opening = formId && courtForms(r.court).find((f) => f.id === formId);
  if (opening && formsStore.history(opening, render) === "loading") return;
  if (r.section !== "cases") refSection = r.section;
  if (r.itemId !== "new") lastCourtId = r.court.id;
  // The Cases page opens the most recent case; with no cases yet it is only its pane, which offers to start one.
  if (r.section === "cases" && !r.itemId && listCases().length) {
    const k = caseOrder()[0];
    window.history.replaceState(null, "", caseHref(k));
    render();
    return;
  }
  // On a narrow screen the list and the pane take turns. A case's own page is its column; its checklist,
  // actions and forms are the pane.
  const hasItem = r.section === "cases" ? !getCase(r.itemId) || !!r.sub : !!r.itemId;
  const prevList = document.getElementById("list-body");
  const prevScroll = prevList ? prevList.scrollTop : 0;
  const prevKey = prevList ? prevList.dataset.key : "";
  const pdfScroll = pdfOpen && pdfOpen.host.querySelector(".pdf-scroll");
  const pdfAt = pdfScroll && [pdfScroll.scrollTop, pdfScroll.scrollLeft];
  app.replaceChildren(
    h("div", { class: "shell", "data-view": hasItem ? "detail" : "list" },
      sidebar(r),
      h("div", { class: "body" }, listColumn(r),
        { cases: casesPane, actions: actionPane, forms: formPane, documents: documentPane }[r.section](r))),
    ...(menuOpen === "feedback" ? [feedbackDialog()] : [])); // replaceChildren would print a null as text
  if (pdfOpen && !pdfOpen.host.isConnected) closePdf();
  else if (pdfAt && pdfScroll.isConnected) [pdfScroll.scrollTop, pdfScroll.scrollLeft] = pdfAt;
  const dlg = app.querySelector("dialog.feedback-modal");
  if (dlg) dlg.showModal();
  if (feedback.focus) { feedback.focus = false; const t = document.getElementById("feedback-text"); if (t) t.focus(); }
  const addInput = app.querySelector(".add-menu .menu-filter");
  if (addInput) addInput.focus();
  if (focusCaseName) { focusCaseName = false; const n = app.querySelector('.card .field input[type="text"]'); if (n) n.focus(); }
  const filter = document.getElementById("court-filter");
  if (filter) {
    filter.focus();
    const sel = document.querySelector("#court-options .sel");
    if (sel) sel.scrollIntoView({ block: "center" });
  }
  const nextList = document.getElementById("list-body");
  const key = `${r.court.id}/${r.section}/${r.section === "cases" ? r.itemId : ""}`;
  if (nextList) {
    nextList.dataset.key = key;
    if (prevKey === key) nextList.scrollTop = prevScroll;
    else { const sel = nextList.querySelector(".sel"); if (sel) sel.scrollIntoView({ block: "center" }); }
    markLetter(nextList);
  }
  const k = r.section === "cases" && getCase(r.itemId);
  const [sec, id] = k && r.sub ? [r.sub, r.subId] : [r.section, r.itemId];
  const item = { actions: courtActions(r.court).find((p) => p.id === id), documents: courtDocuments(r.court).find((d) => d.id === id), forms: courtForms(r.court).find((x) => x.id === id) }[sec];
  const label = [item ? (sec === "forms" ? formName(item) : sec === "actions" ? item.name : item.title) : "", k ? k.name : r.itemId === "new" ? "New case" : ""].filter(Boolean).join(" - ");
  document.title = `${label ? label + " - " : ""}OpenCourt`;
}

document.addEventListener("click", () => { if (menuOpen) { menuOpen = null; courtQuery = ""; render(); } });
// The feedback dialog closes itself on Escape (its cancel event).
document.addEventListener("keydown", (e) => { if (e.key === "Escape" && menuOpen && menuOpen !== "feedback") { menuOpen = null; courtQuery = ""; render(); } });

let lastKey = "";
window.addEventListener("hashchange", () => {
  // Until the content loads, route() has no court to read; the loader renders the current hash itself.
  if (!courts.length) return;
  redirectOldId();
  const r = route();
  const key = `${r.court.id}/${r.section}`;
  if (key !== lastKey) { query = ""; docKind = null; }
  lastKey = key;
  if (menuOpen && menuOpen !== "feedback") menuOpen = null; // menus belong to the page left behind
  render();
});

// What every court needs first; then the forms of the court in the URL, while the rest loads.
const base = Promise.all([load("src/content/courts.json"), load("src/content/issuers.json"), load("src/content/jurisdictions.json")])
  .then(([c, i, j]) => {
    issuers = i; cov = coverage(j); jurisdictionName = Object.fromEntries(j.map((n) => [n.id, n.name]));
    formsStore = formStore(load, i.map((x) => x.id)); forms = formsStore.all;
    return c;
  });
// `courts` is set once everything has loaded (the hashchange handler waits for it), so read the court here. A case's
// URL has no court in it, so its court is the case's own.
const firstForms = base.then((c) => {
  const [, first, second, third] = location.hash.split("/");
  const k = first === "cases" ? getCase(decodeURIComponent(second || "")) : second === "cases" && getCase(decodeURIComponent(third || ""));
  const court = c.find((x) => x.id === (k ? k.court : first)) || c.find((x) => x.id === DEFAULT_COURT) || c[0];
  return court && formsStore.load(formIssuers(court));
});
function loadFailed(err) {
  app.replaceChildren(h("div", { class: "fatal" }, h("h2", null, "Could not load the app data"),
    h("p", null, "Serve this folder over HTTP instead of opening index.html directly. See the README."), h("pre", null, String(err))));
}
Promise.all([load("src/content/actions.json"), load("src/content/documents.json"), load("src/content/form-ids.json"), load("src/content/action-types.json"), load("src/content/action-ids.json"), load("src/content/locations.json"), load("src/content/document-kinds.json"), load("src/content/rule-index.json"), load("src/content/judges.json"), load("src/content/action-provisions.json"), base, firstForms])
  .then(([p, d, fi, pt, pi, l, dk, ri, jg, ap, c]) => {
    courts = c; actions = p; documents = d; formIds = fi; locations = l; judges = jg; actionProvisions = ap;
    actionTypes = Object.fromEntries(pt.map((t) => [t.id, t])); actionIds = pi;
    docKinds = Object.fromEntries(dk.map((k) => [k.id, k]));
    // A cited rule's document links its page when the court shows it.
    configureRules({ ruleIndex: ri, documents: d, documentHref: (courtId, doc) => {
      const court = courts.find((x) => x.id === courtId);
      return court && courtDocuments(court).some((x) => x.id === doc.id) ? `#/${courtId}/documents/${encodeURIComponent(doc.id)}` : null;
    } });
    if (!location.hash) location.hash = CASES_HASH;
    redirectOldId();
    const r = route(); lastKey = `${r.court.id}/${r.section}`;
    render();
  })
  .catch(loadFailed);
